import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";

export interface RouteInfo {
  commodity: string;
  origin: string;
  destination: string;
}

interface AirportTickerProps {
  routes: RouteInfo[];
  interval?: number;
}

export default function AirportTicker({
  routes,
  interval = 4500,
}: AirportTickerProps) {
  const [index, setIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    if (routes.length <= 1) return;
    const timer = setInterval(() => {
      setIsFading(true);
      const timeout = setTimeout(() => {
        setIndex((prev) => (prev + 1) % routes.length);
        setIsFading(false);
      }, 350);
      return () => clearTimeout(timeout);
    }, interval);
    return () => clearInterval(timer);
  }, [routes.length, interval]);

  const route = routes[index];

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      <div className="flex items-center gap-1.5 rounded-full bg-foreground px-2.5 py-1">
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
        </span>
        <span className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-primary-foreground">
          In transit
        </span>
      </div>

      <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
        Live trade routes
      </span>

      <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
        <span
          className={`shrink-0 font-mono text-[9px] font-bold uppercase tracking-tighter text-primary transition-opacity duration-300 ${
            isFading ? "opacity-0" : "opacity-100"
          }`}
        >
          {route.commodity}
        </span>
        <span className="hidden text-muted-foreground sm:inline">·</span>
        <div
          className={`flex min-w-0 items-center gap-1 transition-opacity duration-300 ${
            isFading ? "opacity-0" : "opacity-100"
          }`}
        >
          <span className="truncate font-display text-xs font-semibold uppercase tracking-tight text-foreground sm:text-sm">
            {route.origin}
          </span>
          <ArrowRight
            className="h-3 w-3 shrink-0 text-muted-foreground"
            strokeWidth={2.5}
          />
          <span className="truncate font-display text-xs font-semibold uppercase tracking-tight text-foreground sm:text-sm">
            {route.destination}
          </span>
        </div>
      </div>
    </div>
  );
}
